import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatMarkdown } from "@/components/formatMarkdown";
import useSWR from "swr";
import { Template } from "@/types/template";
import { cn } from "@/lib/utils";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";

export const Boilerplate = ({ landing = false }: { landing?: boolean }) => {
  const { data: templates } = useSWR<Template[]>("/api/template?tag=template&limit=4");

  return (
    <section
      id="boilerplate"
      className={cn(landing ? "container" : "col-span-full", "text-center")}
    >
      <h2 className="text-3xl md:text-4xl font-bold ">
        Building a <span className="gradient-light dark:gradient-dark">new project?</span>
      </h2>
      <p className="md:w-3/4 mx-auto mt-4 mb-8 text-xl text-muted-foreground">
        Start with these production-ready templates to bootstrap your new project.
      </p>

      <Card
        className={cn(
          "bg-gradient-to-br from-slate-950 to-indigo-950 text-white",
          landing && "border"
        )}
      >
        <CardContent>
          {/* Popular Templates Section */}
          <div className="space-y-6">
            <div className="flex flex-col gap-2 justify-between">
              <h2 className="text-xl font-semibold text-left">
                Popular new project boilerplates
              </h2>
              <div className="text-muted-foreground text-left">
                Fork one of these to kickstart your new project and deploy it in minutes,
                without the hassle of setting up a new server.
              </div>
              <div className="flex justify-start mt-2 md:mt-0">
                <Button variant="outline" asChild>
                  <MarketingLink
                    href="/r?tag=template"
                    className="plausible-event-name=ViewAllTemplates hover:bg-accent/10 text-foreground hover:text-primary-foreground"
                  >
                    View all
                  </MarketingLink>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {templates?.map(template => (
                <MarketingLink
                  key={template.id}
                  className="group relative flex hover:scale-105 hover:border-primary/50 transition-all duration-300 flex-col gap-4 p-6 rounded-xl bg-background from-accent/10 to-accent/5 hover:from-accent/15 hover:to-accent/10 border border-accent/10 text-foreground"
                  href={`/r/${template.id}`}
                >
                  <div
                    className="absolute top-0 right-0 h-[50px] w-[50px] overflow-hidden rounded-tr-xl"
                    aria-hidden
                  >
                    <img
                      src="/github-corner.svg"
                      alt=""
                      width={50}
                      height={50}
                      className="block"
                    />
                  </div>
                  <div className="h-14 w-14 rounded-full bg-accent/5 p-2 flex items-center justify-center">
                    {template.logo && (
                      <img
                        src={template.logo}
                        alt={`${template.name} logo`}
                        className="h-10 w-10 object-contain"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg text-left font-semibold line-clamp-1 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-sm text-left text-muted-foreground line-clamp-2">
                      {formatMarkdown(template.intro)}
                    </p>
                  </div>

                  <div className="mt-auto flex min-h-9 items-center gap-2 text-sm">
                    {template.demoUrl && (
                      <span
                        className="flex shrink-0"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        role="presentation"
                      >
                        <Button variant="outline" size="sm" asChild>
                          <ExternalLink
                            href={template.demoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open template demo"
                          >
                            Demo
                          </ExternalLink>
                        </Button>
                      </span>
                    )}
                    <span className="ml-auto flex shrink-0 items-center">
                      Deploy now
                      <svg
                        className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  </div>
                </MarketingLink>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
